import angular from 'angular';

import { notificationFactory } from '@web/common/utils/browserAPI/notification';

import modalMovePostUrl from '../modal-move-post.html';
import MovePostCtrl from '../MovePostCtrl';
import { RESTRICTION_TYPE } from '../constants';

export default function NewsfeedCollection(
  Restangular,
  Authentication,
  StatusService,
  PersonaCollection,
  Utils,
  $rootScope,
  $q,
  $window,
  $uibModal
) {
  'ngInject';

  const service = this;
  let _promise = null;
  const _onChangeHandlers = [];
  let _loadingMoreNotifications;
  let _noMoreNotificationsToLoad;

  // PRIVATE

  function _notifyOnChange() {
    _onChangeHandlers.forEach((h) => h());
  }

  function _clearNotificationData() {
    service.notifications = [];
  }

  function _clearChannelData() {
    service.searchQuery = '';
    service.allChannels = null;
    service.pages = []; // pages are *all channels except for channel 0*, also ones you don't have access to
    service.subscribedPages = []; // all *writable* pages you're subscribed to
    service.unsubscribedPages = []; // all *writable* pages you're not subscribed to
    service.filteredPages = [];
  }

  _clearNotificationData();
  _clearChannelData();

  function _filterChannels(query = '') {
    service.searchQuery = query;
    service.pages = [];
    service.subscribedPages = [];
    service.unsubscribedPages = [];

    const now = new Date();
    service.allChannels.forEach((c) => {
      if (query && !c.name.toLowerCase().includes(query)) {
        return;
      }

      if (c.id !== 0) {
        service.pages.push(c);
      }

      if (c.writable) {
        if (!c.lastPost) {
          c.lastPost = now;
        }

        if (c.subscribed || c.pinned) {
          service.subscribedPages.push(c);
        } else {
          service.unsubscribedPages.push(c);
        }
      }
    });

    const sortPages = (pageA, pageB) => {
      const a = pageA.name ? pageA.name : '';
      const b = pageB.name ? pageB.name : '';
      return b.toLowerCase().localeCompare(a.toLowerCase());
    };

    const sortPinnedPages = (pageA) => (pageA.pinned ? -1 : 1);

    // Sort them (pages are actually ordered in ng-repeat's orderBy anyway)
    service.subscribedPages = service.subscribedPages.sort(sortPages).sort(sortPinnedPages);
    service.unsubscribedPages = service.unsubscribedPages.sort(sortPages);
  }

  function _loadFromServer() {
    Restangular.all('channels/notifications')
      .getList({ 'user-fields': 'id' })
      .then((data) => {
        service.notifications = data;
      });

    return Restangular.all('channels')
      .getList({ 'user-fields': 'id' })
      .then((data) => {
        service.allChannels = data;
        _filterChannels();

        return true;
      });
  }

  service.loadMoreNotifications = async function loadMoreNotifications() {
    if (_loadingMoreNotifications || _noMoreNotificationsToLoad) {
      return;
    }

    _loadingMoreNotifications = true;
    try {
      const data = await Restangular.all('channels/notifications').getList({
        'user-fields': 'id',
        before: Math.min(...service.notifications.map((n) => n.id)),
      });
      if (!data.length) {
        _noMoreNotificationsToLoad = true;
      }

      data.forEach((notification) => {
        if (!service.notifications.find((n) => n.id === notification.id)) {
          service.notifications.push(notification);
        }
      });
    } finally {
      _loadingMoreNotifications = false;
    }
  };

  function _getChannel(channelId) {
    if (!service.allChannels) {
      return false;
    }

    return service.allChannels.find((c) => c.id === channelId);
  }

  function _getArticleInChannel(channelId, articleId) {
    const articles = [];

    let channel = _getChannel(channelId);
    if (channel && channel.articles) {
      const article = channel.articles.find((a) => a.id === articleId);
      if (article) {
        articles.push(article);
      }
    }

    if (channel && channel.subscribed) {
      channel = _getChannel(0);
      if (channel && channel.articles) {
        const article = channel.articles.find((a) => a.id === articleId);
        if (article) {
          articles.push(article);
        }
      }
    }

    return articles;
  }

  function _findArticle(articleId) {
    const articles = [];
    if (service.allChannels) {
      service.allChannels.forEach((page) => {
        if (page.articles) {
          const article = page.articles.find((a) => a.id === articleId);
          if (article) {
            articles.push(article);
          }
        }
      });
    }

    return articles;
  }

  function _getCommentInArticleInChannel(channelId, articleId, commentId) {
    const comments = [];

    const articles = _getArticleInChannel(channelId, articleId);
    if (articles.length) {
      articles.forEach((article) => {
        const comment = article.commentlist.find((c) => c.id === commentId);
        if (comment) {
          comments.push(comment);
        }
      });
    }

    return comments;
  }

  function _shouldNotify(article) {
    if (Authentication.user.settings.newsfeedNotify === 0) {
      return false;
    }

    const subscribedPage = service.subscribedPages.find((p) => p.id === article.channel.id);

    if (subscribedPage) {
      if (Authentication.user.settings.newsfeedNotify === 1) {
        return true;
      }

      if (article.content.indexOf('@') === -1) {
        return false;
      }

      // Is the user mentioned?
      const content = article.content.toLowerCase();
      const checks = [
        `@${Utils.normalizeString(Authentication.user.name.replace(/\s+/g, ''))}[${
          Authentication.user.id
        }]`,
        '@everyone',
        '@all',
      ];

      return checks.some((check) => content.indexOf(check) !== -1);
    }
  }

  function _shouldSendBrowserNotification(toBuffer, article) {
    return (
      toBuffer &&
      Authentication.user.clientsettings.newsfeedNotifications &&
      !StatusService.focus &&
      _shouldNotify(article)
    );
  }

  // PUBLIC

  service.newPosts = {};

  service.onChange = function onChange(handler) {
    _onChangeHandlers.push(handler);

    return function deregisterOnChange() {
      const index = _onChangeHandlers.indexOf(handler);
      if (index >= 0) {
        _onChangeHandlers.splice(index, 1);
      }
    };
  };

  service.clearData = function clearData() {
    _promise = null; // by setting _promise to null we can call loadData again
    _noMoreNotificationsToLoad = false;
    _clearNotificationData();
    _clearChannelData();
  };

  service.setDirty = function setDirty() {
    _promise = null;
  };

  service.loadData = function loadData() {
    if (_promise !== null) {
      return _promise;
    }

    _promise = _loadFromServer();
    return _promise;
  };

  service.refresh = function refresh() {
    if (!__TEST__) {
      console.log('refresh NewsfeedCollection');
    }

    if (service.allChannels) {
      service.clearData();
      return service.loadData();
    }

    return $q.resolve();
  };

  service.addChannel = function addChannel(channel) {
    if (!__TEST__) {
      console.log('addChannel', channel);
    }

    if (service.allChannels) {
      // Check if it doesn't already exist
      const existing = _getChannel(channel.id);
      if (existing) {
        return false;
      }

      // We can set the detailsLoaded flag to true when we are adding a new newsfeed channel
      // since every page is created with no articles to begin with.
      channel.detailsLoaded = true;
      Restangular.restangularizeElement(null, channel, 'channels');
      service.allChannels.push(channel);

      _filterChannels();
      _notifyOnChange();
    }
  };

  service.editChannel = function editChannel(channel) {
    if (!__TEST__) {
      console.log('editChannel', channel);
    }

    const channelInList = _getChannel(channel.id);
    if (channelInList) {
      channel.detailsLoaded = false;
      channel.articles = channelInList.articles;
      angular.extend(channelInList, channel);

      Restangular.restangularizeElement(null, channelInList, 'channels');

      _filterChannels();
      _notifyOnChange();
    }
  };

  service.deleteChannel = function deleteChannel(channel) {
    if (!__TEST__) {
      console.log('deleteChannel', channel);
    }

    if (service.allChannels) {
      service.allChannels = service.allChannels.filter((c) => c.id !== channel.id);
      _filterChannels();
      _notifyOnChange();
    }
  };

  service.getChannelDetails = function getChannelDetails(channel) {
    if (channel.detailsLoaded) {
      return $q.when(channel);
    }

    return Restangular.one('channels', channel.id)
      .get({ 'user-fields': 'id' })
      .then((data) => {
        angular.extend(channel, data);
        Restangular.restangularizeElement(null, channel, 'channels');
        channel.detailsLoaded = true;
        return channel;
      });
  };

  service.getArticle = function getArticle(channel, articleId, httpConfig = {}) {
    if (channel.articles) {
      const article = channel.articles.find((a) => a.id === articleId);
      if (article) {
        return $q.resolve(article);
      }
    }

    return channel
      .one('articles', articleId)
      .withHttpConfig(httpConfig)
      .get({ comments: 20, 'user-fields': 'id' })
      .then((data) => {
        service.addArticle(data, false);
        return data;
      });
  };

  service.addArticle = function addArticle(article, toBuffer) {
    if (!__TEST__) {
      console.log('addArticle', article);
    }

    const channelInList = _getChannel(article.channel.id);
    if (channelInList) {
      let articleArray;

      if (toBuffer) {
        articleArray = channelInList.articleBuffer;
      } else {
        articleArray = channelInList.articles;
      }

      const existing = articleArray.find((a) => a.id === article.id);
      if (existing) {
        return;
      }

      // Add article
      articleArray.push(article);

      // Re-order the articles
      Restangular.restangularizeElement(null, channelInList, 'channels');

      if (channelInList.subscribed) {
        const channelZero = _getChannel(0);

        if (toBuffer) {
          channelZero.articleBuffer.push(article);
        } else {
          channelZero.articles.push(article);
        }

        // Re-order the articles
        Restangular.restangularizeElement(null, channelZero, 'channels');
      }

      channelInList.lastPost = article.posted;

      // browser notifications
      if (_shouldSendBrowserNotification(toBuffer, article)) {
        const content = article.content.replace(/@(\S+)\[(\d+)\]/g, (m, match1) => `@${match1}`);

        const user = PersonaCollection.personasDict[article.user.id];

        notificationFactory(`New post on ${article.channel.name} from ${user.fullname}`, {
          body: content,
          closeTimeout: 3000,
          icon: '/assets/icons/apple-icon.png',
          onClick: () => {
            $window.focus();
          },
        });
      }
    }
  };

  service.editArticle = function editArticle(data) {
    if (!__TEST__) {
      console.log('editArticle', data);
    }

    let articles = _findArticle(data.id);
    if (articles.length && articles[0].channel.id !== data.channel.id) {
      const art = articles[0];
      service.deleteArticle(art);
      Object.assign(art, data);
      service.addArticle(art);
      return;
    }

    articles = _getArticleInChannel(data.channel.id, data.id);
    if (articles.length) {
      articles.forEach((article) => {
        angular.extend(article, data);
        Restangular.restangularizeElement(null, article, 'articles');
      });

      const channelInList = _getChannel(data.channel.id);
      if (channelInList) {
        // Re-order the articles
        Restangular.restangularizeElement(null, channelInList, 'channels');

        if (channelInList.subscribed) {
          const channelZero = _getChannel(0);

          // Re-order the articles
          Restangular.restangularizeElement(null, channelZero, 'channels');
        }
      }
    }
  };

  service.deleteArticle = function deleteArticle(article) {
    if (!__TEST__) {
      console.log('deleteArticle', article);
    }

    const channelInList = _getChannel(article.channel.id);
    if (channelInList) {
      channelInList.articles = channelInList.articles.filter((a) => a.id !== article.id);
      channelInList.articleBuffer = channelInList.articleBuffer.filter((a) => a.id !== article.id);

      // Re-order the articles
      Restangular.restangularizeElement(null, channelInList, 'channels');

      if (channelInList.subscribed) {
        const channelZero = _getChannel(0);
        channelZero.articles = channelZero.articles.filter((a) => a.id !== article.id);
        channelZero.articleBuffer = channelZero.articleBuffer.filter((a) => a.id !== article.id);

        // Re-order the articles
        Restangular.restangularizeElement(null, channelZero, 'channels');
      }
    }
  };

  service.removeArticleNotification = function removeArticleNotification({ article }) {
    const notificationIndex = service.notifications.findIndex(
      (notification) => notification.article.id === article.id
    );
    if (notificationIndex > -1) {
      service.notifications.splice(notificationIndex, 1);
    }
  };

  service.addArticleLike = function addArticleLike(data) {
    if (!__TEST__) {
      console.log('addArticleLike', data);
    }

    const articles = _getArticleInChannel(data.channelId, data.articleId);
    if (articles.length) {
      articles.forEach((article) => {
        article.likes = data.likes;

        if (data.actorId === Authentication.user.id) {
          article.liked = true;
        }
      });
    }
  };

  service.deleteArticleLike = function deleteArticleLike(data) {
    if (!__TEST__) {
      console.log('deleteArticleLike', data);
    }

    const articles = _getArticleInChannel(data.channelId, data.articleId);
    if (articles.length) {
      articles.forEach((article) => {
        article.likes = data.likes;

        if (data.actorId === Authentication.user.id) {
          article.liked = false;
        }
      });
    }
  };

  service.addComment = function addComment(data) {
    if (!__TEST__) {
      console.log('addComment', data);
    }

    const articles = _getArticleInChannel(data.channelId, data.articleId);
    if (articles.length) {
      articles.forEach((article) => {
        const existing = article.commentlist.find((c) => c.id === data.id);
        if (existing) {
          return;
        }

        Restangular.restangularizeElement(null, data, 'comments');
        article.commentlist.push(data);
        article.comments++;
      });
    }
  };

  service.editComment = function editComment(data) {
    if (!__TEST__) {
      console.log('editComment', data);
    }

    const comments = _getCommentInArticleInChannel(data.channelId, data.articleId, data.id);
    if (comments.length) {
      comments.forEach((comment) => {
        angular.extend(comment, data);
        Restangular.restangularizeElement(null, comment, 'comments');
      });
    }
  };

  service.deleteComment = function deleteComment(data) {
    if (!__TEST__) {
      console.log('deleteComment', data);
    }

    const articles = _getArticleInChannel(data.channelId, data.articleId);
    if (articles.length) {
      articles.forEach((article) => {
        article.commentlist = article.commentlist.filter((c) => c.id !== data.id);
        article.comments--;
      });
    }
  };

  service.addCommentLike = function addCommentLike(data) {
    if (!__TEST__) {
      console.log('addCommentLike', data);
    }

    const comments = _getCommentInArticleInChannel(data.channelId, data.articleId, data.commentId);
    if (comments.length) {
      comments.forEach((comment) => {
        comment.likes = data.likes;

        if (data.actorId === Authentication.user.id) {
          comment.liked = true;
        }
      });
    }
  };

  service.deleteCommentLike = function deleteCommentLike(data) {
    if (!__TEST__) {
      console.log('deleteCommentLike', data);
    }

    const comments = _getCommentInArticleInChannel(data.channelId, data.articleId, data.commentId);
    if (comments.length) {
      comments.forEach((comment) => {
        comment.likes = data.likes;

        if (data.actorId === Authentication.user.id) {
          comment.liked = false;
        }
      });
    }
  };

  service.removeLikeNotification = function removeLikeNotification(data) {
    const notificationIndex = service.notifications.findIndex(
      (notification) =>
        notification.type === 'like' &&
        notification.actor.id === data.actor.id &&
        notification.article.id === data.article.id
    );
    if (notificationIndex > -1) {
      service.notifications.splice(notificationIndex, 1);
    }
  };

  service.subscribe = async function subscribe(page) {
    await page.subscribe();
    _filterChannels();

    const idx = page.subscribers.findIndex((user) => user.id === Authentication.user.id);
    if (idx === -1) {
      page.subscribers.push(Authentication.user);
      page.subscriberCount++;
    }
  };

  service.unsubscribe = async function unsubscribe(page) {
    await page.unsubscribe();
    _filterChannels();

    const idx = page.subscribers.findIndex((user) => user.id === Authentication.user.id);
    if (idx > -1) {
      page.subscribers.splice(idx, 1);
      page.subscriberCount--;
    }
  };

  service.moveArticle = function moveArticle(article) {
    return $uibModal.open({
      templateUrl: modalMovePostUrl,
      controller: MovePostCtrl,
      controllerAs: 'ctrl',
      size: '700',
      backdrop: 'static',
      resolve: {
        post: () => article,
      },
    }).result;
  };

  if (__TEST__) {
    service._filterChannels = _filterChannels;
  }

  // TODO those two to sdk
  service.canPageBeCreatedBy = function canPageBeCreatedBy(user) {
    const {
      isManager,
      orgSettings: { usersCanCreatePages },
    } = user;

    return isManager || usersCanCreatePages;
  };

  service.canPostArticle = function canPostArticle(page, user) {
    if (user.isAdmin) {
      return true;
    }

    if (
      page.restricted === RESTRICTION_TYPE.SELECTED_ONLY &&
      !page.restrictedToPersonas.includes(user.id)
    ) {
      return false;
    }

    if (page.restricted === RESTRICTION_TYPE.PRIVILEGED_ONLY && !user.isManager) {
      return false;
    }

    return true;
  };

  service.employeeCanActOnPage = function employeeCanActOnPage(page, user) {
    if (user.isManager) {
      return true;
    }

    const isAnyManagerAssigned = page.members.some((member) => member.isManager);
    return user.orgSettings.usersCanCreatePages || isAnyManagerAssigned;
  };

  service.searchPages = (query = '') => {
    _filterChannels(query);
  };

  Object.defineProperty(service, 'isEmptyNewsfeed', {
    get() {
      return service.allChannels && !service.allChannels.length;
    },
  });

  // Real time socket updates

  $rootScope.$on('socket:channel:new', (event, data) => {
    console.log('channel:new');
    service.addChannel(data);
  });

  $rootScope.$on('socket:channel:edit', (event, data) => {
    console.log('channel:edit');
    service.editChannel(data);
  });

  $rootScope.$on('socket:channel:delete', (event, data) => {
    console.log('channel:delete');
    service.deleteChannel(data);
  });

  $rootScope.$on('socket:article:new', (event, data) => {
    console.log('article:new');
    service.addArticle(data, true);
  });

  $rootScope.$on('socket:article:edit', (event, data) => {
    console.log('article:edit');
    service.editArticle(data);
  });

  $rootScope.$on('socket:article:delete', (event, data) => {
    console.log('article:delete');
    service.deleteArticle(data);
  });

  $rootScope.$on('socket:like:new', (event, data) => {
    console.log('like:new');
    service.addArticleLike(data);
  });

  $rootScope.$on('socket:like:delete', (event, data) => {
    console.log('like:delete', data);
    service.deleteArticleLike(data);
  });

  $rootScope.$on('socket:comment:new', (event, data) => {
    console.log('comment:new');
    service.addComment(data);
  });

  $rootScope.$on('socket:comment:edit', (event, data) => {
    console.log('comment:edit');
    service.editComment(data);
  });

  $rootScope.$on('socket:comment:delete', (event, data) => {
    console.log('comment:delete');
    service.deleteComment(data);
  });

  $rootScope.$on('socket:comment_like:new', (event, data) => {
    console.log('comment_like:new', data);
    service.addCommentLike(data);
  });

  $rootScope.$on('socket:comment_like:delete', (event, data) => {
    console.log('comment_like:delete', data);
    service.deleteCommentLike(data);
  });

  $rootScope.$on('socket:notification:newsfeed', (event, data) => {
    console.log('notification:newsfeed', data);
    if (service.notifications) {
      if (data.type === 'like_delete') {
        service.removeLikeNotification(data);
      } else if (data.type === 'article_delete') {
        service.removeArticleNotification(data);
      } else {
        Restangular.restangularizeElement(null, data, 'channels/notifications');
        service.notifications.unshift(data);
      }
    }
  });

  $rootScope.$on('socket:seen:new', (event, data) => {
    console.log('seen:new', data);

    const articles = _getArticleInChannel(data.channelId, data.articleId);
    if (articles.length) {
      articles.forEach((article) => {
        article.seenby = data.seenby;
      });
    }
  });
}
